// ===================================================================
// Admin — Prova objetiva (fase 1): folha escaneada por inscrição + notas
// ===================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { styles } from '../../styles/globalStyles';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { Modal } from '../../components/ui/Modal';
import { apiClient } from '../../api/apiClient';
import { useAdminProcess } from '../../contexts/AdminProcessContext';
import { parseAdminSelectedProcessId } from '../../utils/adminProcessId';
import {
  initialAdminTableSort,
  nextAdminTableSort,
  sortApplicationsForDisplay,
  sortColumnIndicator,
} from '../../utils/adminTableSort';
import { useNotifications } from '../../contexts/NotificationContext';
import { ExamGradeEntryModal } from '../../components/admin/ExamGradeEntryModal';
import { enrichApplicationForGradeModal } from '../../utils/examObjectiveScoring';
import { ExamScanCellActions } from '../../components/admin/ExamScanCellActions';
import { ExamObjectiveScoreCell } from '../../components/admin/ExamObjectiveScoreCell';
import { ExamPendingScansPanel } from '../../components/admin/ExamPendingScansPanel';
import { ExamOmrCalibrationModal } from '../../components/admin/ExamOmrCalibrationModal';
import {
  DEFINITIVE_ANSWER_KEY_REQUIRED_MSG,
  processDefinitiveAnswerKeyReady,
} from '../../utils/examAnswerKeyPolicy';
import {
  getButtonStyle,
  getStatusBadgeStyle,
  getAssignmentStatusLabel,
  ACTION_ICONS,
} from '../../constants/statusTranslations';
import { examObjectiveStatusLabel, examStatusStyle } from '../../utils/examAnalystWorkflow';

import { INGRESSA_STORAGE_KEYS } from '../../utils/ingressaBrand';

const STORAGE_KEY_EXAM_OBJECTIVE = INGRESSA_STORAGE_KEYS.filtersExamObjective;

const getPaymentStatusForExamAdmin = (application) => {
  if (!application) return 'pending';
  if (application.payment_status === 'paid') return 'paid';
  if (application.exemption_status) {
    switch (application.exemption_status) {
      case 'approved':
        return 'exempted';
      case 'rejected':
        return 'exemption_rejected';
      case 'returned':
        return 'exemption_returned';
      case 'appealed':
        return 'exemption_appealed';
      case 'appeal_approved':
        return 'exemption_appeal_approved';
      case 'appeal_rejected':
        return 'exemption_appeal_rejected';
      case 'pending': {
        const hasExemptionAnalyst = application.exemption_analyst_id || application.exemption_analyst_name;
        if (!hasExemptionAnalyst) return 'pending_exemption_not_assigned';
        return application.exemption_request_status === 'analyzing'
          ? 'pending_exemption'
          : 'pending_exemption_assigned';
      }
      default:
        break;
    }
  }
  if (application.payment_status) return application.payment_status;
  return 'pending';
};

const appPaymentOkForExamAssign = (app) => {
  const ps = getPaymentStatusForExamAdmin(app);
  return ps === 'paid' || ps === 'exempted' || ps === 'exemption_appeal_approved';
};

const fmtNota = (v, scoreMode) => {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  const s = n.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 2 });
  return `${s} pts`;
};

const gradeSourceLabel = (app) => {
  const src = (app.exam_grade_source || '').toLowerCase();
  if (src === 'system') return 'Sistema';
  if (app.exam_grade_recorded_by_name) return app.exam_grade_recorded_by_name;
  if (app.exam_final_score != null && app.exam_grades_updated_at) return '—';
  return '—';
};

const appPaymentOkForEssay = (app) => {
  const ex = app.exemption_status || '';
  if (ex === 'approved' || ex === 'appeal_approved') return true;
  return (app.payment_status || '') === 'paid';
};

const essayCutDisplay = (app) => {
  if (!appPaymentOkForEssay(app)) {
    return { label: 'Não', detail: 'pagamento', color: '#9CA3AF' };
  }
  if (app.exam_objective_disqualified_zero) {
    return { label: 'Não', detail: 'zero em área', color: '#B91C1C' };
  }
  if (app.exam_final_score == null) {
    return { label: 'Não', detail: 'sem nota', color: '#9CA3AF' };
  }
  if (app.essay_correction_eligible) {
    const rank = app.essay_for_correction_rank;
    return { label: 'Sim', detail: rank != null ? `#${rank}` : '—', color: '#166534' };
  }
  return { label: 'Não', detail: 'fora do corte', color: '#6B7280' };
};

const essayEligSummaryText = (elig) => {
  if (!elig?.ok) return '';
  const n = elig.eligible_count ?? 0;
  const withScore = elig.with_objective_score ?? 0;
  const mult = elig.multiplier ?? '?';
  return `${n} elegível(is) à correção da redação (${withScore} com nota objetiva; corte = multiplicador ×${mult} × vagas — grupos AC e cotas).`;
};

export const ExamObjectiveAdminPage = React.memo(() => {
  const { selectedProcessId, setSelectedProcessId, processes, refreshProcesses } = useAdminProcess();
  const { addNotification } = useNotifications();
  const fileInputRef = useRef(null);
  const batchInputRef = useRef(null);
  const omrCalFileInputRef = useRef(null);
  const batchProcessIdRef = useRef(null);
  const uploadTargetIdRef = useRef(null);
  const batchFileInputId = 'exam-objective-batch-file-input';
  const [scanActionMsg, setScanActionMsg] = useState(null);
  const [batchBusy, setBatchBusy] = useState(false);
  const [omrBusy, setOmrBusy] = useState(false);
  const [omrCalibrateOpen, setOmrCalibrateOpen] = useState(false);
  const [omrCalSummary, setOmrCalSummary] = useState(null);
  const [omrCalUpload, setOmrCalUpload] = useState(null);
  const [pendingRows, setPendingRows] = useState([]);
  const [pendingLinkDraft, setPendingLinkDraft] = useState({});
  const [pendingBusy, setPendingBusy] = useState(null);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analysts, setAnalysts] = useState([]);
  const [gradeApp, setGradeApp] = useState(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [assignmentApp, setAssignmentApp] = useState(null);
  const [selectedAnalyst, setSelectedAnalyst] = useState('');
  const [assignmentIsReassign, setAssignmentIsReassign] = useState(false);
  const [assignmentModalError, setAssignmentModalError] = useState('');
  const [selectedForBulk, setSelectedForBulk] = useState(new Set());
  const [bulkAnalyst, setBulkAnalyst] = useState('');
  const [equitableAnalysts, setEquitableAnalysts] = useState(new Set());
  const [filterCandidate, setFilterCandidate] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY_EXAM_OBJECTIVE);
      if (s) return String(JSON.parse(s).filterCandidate || '');
    } catch (e) {}
    return '';
  });
  const [filterAnalyst, setFilterAnalyst] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY_EXAM_OBJECTIVE);
      if (s) return String(JSON.parse(s).filterAnalyst || '');
    } catch (e) {}
    return '';
  });
  const [filterCourse, setFilterCourse] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY_EXAM_OBJECTIVE);
      if (s) return String(JSON.parse(s).filterCourse || '');
    } catch (e) {}
    return '';
  });
  const [filterCampus, setFilterCampus] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY_EXAM_OBJECTIVE);
      if (s) return String(JSON.parse(s).filterCampus || '');
    } catch (e) {}
    return '';
  });
  const [filterQuota, setFilterQuota] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY_EXAM_OBJECTIVE);
      if (s) return String(JSON.parse(s).filterQuota || '');
    } catch (e) {}
    return '';
  });
  const [filterAssignment, setFilterAssignment] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY_EXAM_OBJECTIVE);
      if (s) return String(JSON.parse(s).filterAssignment || '');
    } catch (e) {}
    return '';
  });
  const [scanBusy, setScanBusy] = useState(null);
  const [tableSort, setTableSort] = useState(() => initialAdminTableSort());
  const [essayEligSummary, setEssayEligSummary] = useState(null);
  const [eligRecalcBusy, setEligRecalcBusy] = useState(false);

  const provaProcesses = useMemo(
    () => (processes || []).filter((p) => p.selection_mode === 'prova' && p.exam_uses_answer_sheet),
    [processes]
  );

  const selectedProcessMeta = useMemo(() => {
    const { id: pid } = parseAdminSelectedProcessId(selectedProcessId);
    if (pid == null) return null;
    return (processes || []).find((p) => Number(p.id) === Number(pid)) || null;
  }, [processes, selectedProcessId]);

  const manualGrades = Boolean(selectedProcessMeta?.release_grades_manual);
  const autoGrades = Boolean(selectedProcessMeta?.release_grades_automatic_scanned);
  const definitiveKeyReady = processDefinitiveAnswerKeyReady(selectedProcessMeta);
  const usesEssay = Boolean(selectedProcessMeta?.exam_uses_essay);
  const essayMultiplier = selectedProcessMeta?.exam_essay_correction_multiplier ?? 4;

  const applyEssayEligFromResponse = useCallback(
    (elig, { notify = false, notifyTitle = 'Corte da redação' } = {}) => {
      if (!elig?.ok) return;
      setEssayEligSummary(elig);
      if (notify) {
        const txt = essayEligSummaryText(elig);
        if (txt) addNotification(notifyTitle, txt, 'success');
      }
    },
    [addNotification]
  );

  const selectedPid = useMemo(() => {
    const { id } = parseAdminSelectedProcessId(selectedProcessId);
    return id;
  }, [selectedProcessId]);

  useEffect(() => {
    batchProcessIdRef.current = selectedPid;
  }, [selectedPid]);

  useEffect(() => {
    if (!autoGrades || selectedPid == null) {
      setOmrCalSummary(null);
      return;
    }
    const fromMeta = selectedProcessMeta?.exam_omr_calibration;
    if (fromMeta && typeof fromMeta === 'object') {
      setOmrCalSummary(fromMeta);
    }
    let cancelled = false;
    (async () => {
      const { data } = await apiClient.getProcessExamOmrCalibration(selectedPid);
      if (!cancelled && data) setOmrCalSummary(data);
    })();
    return () => {
      cancelled = true;
    };
  }, [autoGrades, selectedPid, selectedProcessMeta?.exam_omr_calibration]);

  useEffect(() => {
    refreshProcesses();
  }, [refreshProcesses]);

  useEffect(() => {
    if (provaProcesses.length === 0 || !selectedProcessId) return;
    const ok = provaProcesses.some((p) => String(p.id) === String(selectedProcessId));
    if (!ok) {
      setSelectedProcessId(String(provaProcesses[0].id));
    }
  }, [provaProcesses, selectedProcessId, setSelectedProcessId]);

  const warnBatchUploadBlocked = useCallback(() => {
    if (selectedPid == null) {
      addNotification(
        'Selecione o processo',
        'Escolha o processo seletivo (por prova com gabarito) antes de enviar o lote de PDFs.',
        'warning'
      );
      return;
    }
    if (!definitiveKeyReady) {
      addNotification('Gabarito definitivo', DEFINITIVE_ANSWER_KEY_REQUIRED_MSG, 'warning');
    }
  }, [selectedPid, definitiveKeyReady, addNotification]);

  const batchUploadReady = selectedPid != null && definitiveKeyReady && !batchBusy;

  const load = useCallback(async () => {
    const { id: pid, empty, invalid } = parseAdminSelectedProcessId(selectedProcessId);
    if (empty || invalid || pid == null) {
      setRows([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await apiClient.getApplications({ processId: pid, forExamObjective: true });
      const list = Array.isArray(data) ? data : [];
      setRows(list.filter((a) => !a.excluded_at));
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [selectedProcessId]);

  const loadPending = useCallback(async () => {
    const { id: pid, empty, invalid } = parseAdminSelectedProcessId(selectedProcessId);
    if (empty || invalid || pid == null) {
      setPendingRows([]);
      return;
    }
    try {
      const list = await apiClient.getExamObjectiveScansPending(pid);
      setPendingRows(Array.isArray(list) ? list : []);
    } catch {
      setPendingRows([]);
    }
  }, [selectedProcessId]);

  useEffect(() => {
    load();
    loadPending();
  }, [load, loadPending]);

  useEffect(() => {
    setTableSort(initialAdminTableSort());
    setEssayEligSummary(null);
    setSelectedForBulk(new Set());
    setBulkAnalyst('');
    setEquitableAnalysts(new Set());
  }, [selectedProcessId]);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        STORAGE_KEY_EXAM_OBJECTIVE,
        JSON.stringify({
          filterCandidate: String(filterCandidate || ''),
          filterAnalyst: String(filterAnalyst || ''),
          filterCourse: String(filterCourse || ''),
          filterCampus: String(filterCampus || ''),
          filterQuota: String(filterQuota || ''),
          filterAssignment: String(filterAssignment || ''),
        })
      );
    } catch (e) {}
  }, [filterCandidate, filterAnalyst, filterCourse, filterCampus, filterQuota, filterAssignment]);

  const handleRecalcEssayEligibility = useCallback(async () => {
    const { id: pid } = parseAdminSelectedProcessId(selectedProcessId);
    if (pid == null) return;
    setEligRecalcBusy(true);
    try {
      const { data, error } = await apiClient.recalculateEssayEligibility(pid);
      if (error) throw new Error(error);
      applyEssayEligFromResponse(data, { notify: true, notifyTitle: 'Corte recalculado' });
      await load();
    } catch (err) {
      addNotification('Erro', err.message || 'Falha ao recalcular corte', 'error');
    } finally {
      setEligRecalcBusy(false);
    }
  }, [selectedProcessId, addNotification, load, applyEssayEligFromResponse]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await apiClient.getUsers('analyst');
        if (!cancelled) setAnalysts(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setAnalysts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const analystsForProcess = useMemo(() => {
    const { empty, invalid, id: pid } = parseAdminSelectedProcessId(selectedProcessId);
    if (empty) return analysts;
    if (invalid || pid == null) return [];
    return analysts.filter((a) => {
      const pids = a.process_ids || [];
      return pids.length === 0 || pids.includes(pid);
    });
  }, [analysts, selectedProcessId]);

  const analystFilterOptions = useMemo(() => {
    const byId = new Map();
    analystsForProcess.forEach((a) => byId.set(String(a.id), { id: a.id, name: a.name || `Usuário #${a.id}` }));
    rows.forEach((app) => {
      const id = app.exam_grade_analyst_id;
      if (!id) return;
      const key = String(id);
      if (byId.has(key)) return;
      byId.set(key, { id, name: app.exam_grade_analyst_name || `Usuário #${id}` });
    });
    return Array.from(byId.values()).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR'));
  }, [rows, analystsForProcess]);

  const courseFilterOptions = useMemo(() => {
    const names = new Set();
    rows.forEach((app) => {
      if (app.course_name) names.add(String(app.course_name));
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [rows]);

  const campusFilterOptions = useMemo(() => {
    const campuses = new Set();
    rows.forEach((app) => {
      if (app.course_campus) campuses.add(String(app.course_campus));
    });
    return Array.from(campuses).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [rows]);

  const quotaFilterOptions = useMemo(() => {
    const quotas = new Set();
    rows.forEach((app) => {
      quotas.add(String(app.quota_type || 'AC'));
    });
    return Array.from(quotas).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const cand = (filterCandidate || '').trim().toLowerCase();
    return rows.filter((app) => {
      if (filterAnalyst) {
        if (filterAnalyst === 'none') {
          if (app.exam_grade_analyst_id) return false;
        } else if (String(app.exam_grade_analyst_id || '') !== filterAnalyst) return false;
      }
      if (filterAssignment === 'assigned' && !app.exam_grade_analyst_id) return false;
      if (filterAssignment === 'not_assigned' && app.exam_grade_analyst_id) return false;
      if (filterAssignment === 'eligible' && !appPaymentOkForExamAssign(app)) return false;
      if (filterCourse && String(app.course_name || '') !== filterCourse) return false;
      if (filterCampus && String(app.course_campus || '') !== filterCampus) return false;
      if (filterQuota && String(app.quota_type || 'AC') !== filterQuota) return false;
      if (cand) {
        const name = (app.candidate_name || '').toLowerCase();
        const cpf = (app.candidate_cpf || '').replace(/\D/g, '');
        const candCpf = cand.replace(/\D/g, '');
        if (!name.includes(cand) && !(candCpf && cpf.includes(candCpf))) return false;
      }
      return true;
    });
  }, [
    rows,
    filterAnalyst,
    filterAssignment,
    filterCourse,
    filterCampus,
    filterQuota,
    filterCandidate,
  ]);

  const sortedRows = useMemo(
    () => sortApplicationsForDisplay(filteredRows, tableSort),
    [filteredRows, tableSort]
  );

  const canAssignExamGrader = useCallback(
    (app) => manualGrades && appPaymentOkForExamAssign(app) && !app.exam_grade_analyst_id,
    [manualGrades]
  );

  const canReassignExamGrader = useCallback(
    (app) => manualGrades && appPaymentOkForExamAssign(app) && !!app.exam_grade_analyst_id,
    [manualGrades]
  );

  const pendingForBulk = useMemo(
    () => filteredRows.filter((app) => canAssignExamGrader(app)),
    [filteredRows, canAssignExamGrader]
  );

  const clearAdminFilters = useCallback(() => {
    setFilterCandidate('');
    setFilterAnalyst('');
    setFilterCourse('');
    setFilterCampus('');
    setFilterQuota('');
    setFilterAssignment('');
  }, []);

  const openAssign = useCallback((app, preselectCurrent = false) => {
    if (!manualGrades) {
      addNotification('Aviso', 'Ative lançamento manual de notas no cadastro do processo.', 'warning');
      return;
    }
    setAssignmentApp(app);
    setAssignmentIsReassign(!!preselectCurrent);
    setSelectedAnalyst(preselectCurrent && app.exam_grade_analyst_id ? String(app.exam_grade_analyst_id) : '');
    setAssignmentModalError('');
    setShowAssignmentModal(true);
  }, [manualGrades, addNotification]);

  const submitAssignment = useCallback(async () => {
    if (!assignmentApp) return;
    if (!selectedAnalyst) {
      setAssignmentModalError('Selecione um analista');
      return;
    }
    if (
      assignmentIsReassign &&
      Number(assignmentApp.exam_grade_analyst_id) === parseInt(selectedAnalyst, 10)
    ) {
      setAssignmentModalError('A inscrição já está atribuída a este analista');
      addNotification('Atenção', 'Selecione outro analista para reatribuir.', 'warning');
      return;
    }
    try {
      const { error } = await apiClient.assignExamGrader(assignmentApp.id, parseInt(selectedAnalyst, 10));
      if (error) throw new Error(error);
      const an = analystsForProcess.find((x) => String(x.id) === selectedAnalyst);
      addNotification(
        'Sucesso',
        `Analista ${an?.name || selectedAnalyst} atribuído para lançamento de notas objetivas`,
        'success'
      );
      setShowAssignmentModal(false);
      setAssignmentApp(null);
      setAssignmentIsReassign(false);
      setSelectedAnalyst('');
      await load();
    } catch (e) {
      setAssignmentModalError(e.message || 'Erro ao atribuir');
      addNotification('Erro', e.message || 'Erro ao atribuir', 'error');
    }
  }, [assignmentApp, selectedAnalyst, assignmentIsReassign, analystsForProcess, addNotification, load]);

  const handleBulkAssign = useCallback(async () => {
    if (!bulkAnalyst || selectedForBulk.size === 0) {
      addNotification('Atenção', 'Selecione um analista e ao menos uma inscrição', 'warning');
      return;
    }
    const appIds = Array.from(selectedForBulk)
      .map((id) => filteredRows.find((x) => x.id === id))
      .filter((app) => app && canAssignExamGrader(app))
      .map((app) => app.id);
    try {
      const { data, error } = await apiClient.bulkAssignExamGrader(appIds, parseInt(bulkAnalyst, 10));
      if (error) throw new Error(error);
      addNotification('Atribuição em massa', `${data?.assigned_count ?? 0} inscrição(ões) atribuída(s)`, 'success');
      setSelectedForBulk(new Set());
      setBulkAnalyst('');
      await load();
    } catch (e) {
      addNotification('Erro', e.message || 'Falha na atribuição em massa', 'error');
    }
  }, [bulkAnalyst, selectedForBulk, filteredRows, canAssignExamGrader, addNotification, load]);

  const handleBulkAssignEquitable = useCallback(async () => {
    if (selectedForBulk.size === 0 || equitableAnalysts.size < 2) {
      addNotification('Atenção', 'Selecione inscrições e marque ao menos 2 analistas para distribuição equânime.', 'warning');
      return;
    }
    const appIds = Array.from(selectedForBulk)
      .map((id) => filteredRows.find((x) => x.id === id))
      .filter((app) => app && canAssignExamGrader(app))
      .map((app) => app.id);
    const analystIds = Array.from(equitableAnalysts)
      .map((x) => parseInt(x, 10))
      .filter((n) => !Number.isNaN(n))
      .sort((a, b) => a - b);
    try {
      const { data, error } = await apiClient.bulkAssignExamGraderEquitable(appIds, analystIds);
      if (error) throw new Error(error);
      const parts = Object.entries(data?.per_analyst || {})
        .filter(([, c]) => c > 0)
        .map(([id, c]) => {
          const an = analystsForProcess.find((x) => String(x.id) === String(id));
          return `${an?.name || id}: ${c}`;
        });
      addNotification(
        'Distribuição equânime',
        `${data?.assigned_count ?? 0} inscrição(ões) atribuída(s)${parts.length ? ` — ${parts.join('; ')}` : ''}`,
        'success'
      );
      setSelectedForBulk(new Set());
      setEquitableAnalysts(new Set());
      await load();
    } catch (e) {
      addNotification('Erro', e.message || 'Falha na distribuição equânime', 'error');
    }
  }, [selectedForBulk, equitableAnalysts, filteredRows, canAssignExamGrader, analystsForProcess, addNotification, load]);

  const triggerUpload = useCallback(
    (appId) => {
      if (!definitiveKeyReady) {
        addNotification('Gabarito definitivo', DEFINITIVE_ANSWER_KEY_REQUIRED_MSG, 'warning');
        return;
      }
      uploadTargetIdRef.current = appId;
      fileInputRef.current?.click();
    },
    [definitiveKeyReady, addNotification]
  );

  const handleFileSelected = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      const appId = uploadTargetIdRef.current;
      uploadTargetIdRef.current = null;
      if (!file) return;
      if (!appId) {
        addNotification(
          'Erro',
          'Não foi possível identificar a inscrição. Clique em Enviar PDF na linha do candidato e tente de novo.',
          'error'
        );
        return;
      }
      setScanBusy(appId);
      try {
        const { data, error } = await apiClient.uploadExamObjectiveScan(appId, file);
        if (error) throw new Error(error);
        if (data) {
          setRows((prev) =>
            prev.map((r) => (Number(r.id) === Number(appId) ? { ...r, ...data } : r))
          );
        }
        const omr = data?.omr_message;
        applyEssayEligFromResponse(data?.essay_eligibility);
        const eligTxt = essayEligSummaryText(data?.essay_eligibility);
        const disqHint = data?.exam_objective_disqualified_zero
          ? ' Há zero acertos em área com peso — a nota aparece na coluna, com indicação 0 em área*.'
          : '';
        const scoreHint =
          data?.exam_final_score != null
            ? ` Nota objetiva: ${Number(data.exam_final_score).toLocaleString('pt-BR', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 2,
              })} pts.`
            : '';
        const msg = [
          omr ? `PDF vinculado. ${omr}` : `PDF vinculado à inscrição #${appId}.`,
          scoreHint,
          disqHint,
          eligTxt,
        ]
          .filter(Boolean)
          .join(' ');
        setScanActionMsg({ type: omr && omr.includes('OMR:') ? 'warning' : 'success', text: msg });
        addNotification('Folha enviada', msg, omr && omr.includes('OMR:') ? 'warning' : 'success');
        await load();
        await loadPending();
      } catch (err) {
        const errMsg = err.message || 'Falha no envio';
        setScanActionMsg({ type: 'error', text: errMsg });
        addNotification('Erro', errMsg, 'error');
      } finally {
        setScanBusy(null);
      }
    },
    [addNotification, load, loadPending, applyEssayEligFromResponse]
  );

  const handleBatchSelected = useCallback(
    async (e) => {
      const files = Array.from(e.target.files || []);
      e.target.value = '';
      const pid =
        batchProcessIdRef.current ??
        parseAdminSelectedProcessId(selectedProcessId).id;
      if (!files.length) {
        addNotification('Importação em lote', 'Nenhum arquivo selecionado.', 'warning');
        return;
      }
      if (pid == null) {
        addNotification(
          'Importação em lote',
          'Selecione o processo seletivo antes de enviar os PDFs.',
          'warning'
        );
        return;
      }
      setBatchBusy(true);
      setScanActionMsg({
        type: 'info',
        text: `Enviando ${files.length} PDF(s)… Aguarde, o processamento pode levar alguns minutos.`,
      });
      try {
        const { data, error } = await apiClient.batchUploadExamObjectiveScans(pid, files);
        if (error) throw new Error(error);
        const linked = data?.linked_count ?? 0;
        const pend = data?.pending_count ?? 0;
        const errN = data?.error_count ?? 0;
        applyEssayEligFromResponse(data?.essay_eligibility);
        const eligTxt = essayEligSummaryText(data?.essay_eligibility);
        const summary = [`${linked} vinculado(s), ${pend} pendente(s), ${errN} erro(s).`, eligTxt]
          .filter(Boolean)
          .join(' ');
        setScanActionMsg({
          type: pend || errN ? 'warning' : 'success',
          text: `Lote concluído: ${summary}`,
        });
        addNotification('Lote processado', summary, pend || errN ? 'warning' : 'success');
        await load();
        await loadPending();
      } catch (err) {
        const errMsg = err.message || 'Falha no lote';
        setScanActionMsg({ type: 'error', text: errMsg });
        addNotification('Erro', errMsg, 'error');
      } finally {
        setBatchBusy(false);
      }
    },
    [selectedProcessId, addNotification, load, loadPending, applyEssayEligFromResponse]
  );

  const uploadOmrCalibrationFile = useCallback(
    async (file) => {
      const { id: pid } = parseAdminSelectedProcessId(selectedProcessId);
      if (!file || pid == null) return;
      const { data, error } = await apiClient.uploadProcessExamOmrCalibrationReference(pid, file);
      if (error) {
        addNotification('Calibração OMR', error, 'error');
        return;
      }
      setOmrCalUpload({
        reference_filename: data.reference_filename,
        review: data.review,
        previewVersion: Date.now(),
        staging: Boolean(data.staging),
      });
      setOmrCalibrateOpen(true);
    },
    [selectedProcessId, addNotification]
  );

  const handleOmrCalFileSelected = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (file) uploadOmrCalibrationFile(file);
    },
    [uploadOmrCalibrationFile]
  );

  const handleCalibrarOmrClick = useCallback(() => {
    if (!definitiveKeyReady) {
      addNotification('Gabarito definitivo', DEFINITIVE_ANSWER_KEY_REQUIRED_MSG, 'warning');
      return;
    }
    omrCalFileInputRef.current?.click();
  }, [definitiveKeyReady, addNotification]);

  const handleRunOmrBatch = useCallback(async () => {
    const { id: pid } = parseAdminSelectedProcessId(selectedProcessId);
    if (pid == null) return;
    setOmrBusy(true);
    try {
      const { data, error } = await apiClient.runExamObjectiveOmrBatch(pid);
      if (error) throw new Error(error);
      const n = data?.processed ?? 0;
      const fail = (data?.failed || []).length;
      const skipped = data?.skipped_count ?? (data?.skipped_manual || []).length;
      applyEssayEligFromResponse(data?.essay_eligibility);
      const eligTxt = essayEligSummaryText(data?.essay_eligibility);
      addNotification(
        'OMR',
        [
          `${n} inscrição(ões) com nota automática.`,
          data?.omr_calibration_applied ? 'Calibração do processo aplicada.' : '',
          skipped ? `${skipped} preservada(s) (já conferida pelo analista/admin).` : '',
          fail ? `${fail} falha(s).` : '',
          eligTxt,
        ]
          .filter(Boolean)
          .join(' '),
        fail ? 'warning' : 'success'
      );
      await load();
    } catch (err) {
      addNotification('Erro', err.message || 'Falha no OMR', 'error');
    } finally {
      setOmrBusy(false);
    }
  }, [selectedProcessId, addNotification, load, applyEssayEligFromResponse]);

  const handleLinkPending = useCallback(
    async (pendingId) => {
      const { id: pid } = parseAdminSelectedProcessId(selectedProcessId);
      const raw = pendingLinkDraft[pendingId];
      if (pid == null || !raw) {
        addNotification('Informe a inscrição', 'Digite o número da inscrição para vincular.', 'warning');
        return;
      }
      setPendingBusy(pendingId);
      try {
        const { data, error } = await apiClient.linkExamObjectiveScanPending(pid, pendingId, Number(raw));
        if (error) throw new Error(error);
        applyEssayEligFromResponse(data?.essay_eligibility);
        const eligTxt = essayEligSummaryText(data?.essay_eligibility);
        addNotification('Vinculado', ['Folha associada à inscrição.', eligTxt].filter(Boolean).join(' '), 'success');
        setPendingLinkDraft((d) => {
          const next = { ...d };
          delete next[pendingId];
          return next;
        });
        await load();
        await loadPending();
      } catch (err) {
        addNotification('Erro', err.message || 'Falha ao vincular', 'error');
      } finally {
        setPendingBusy(null);
      }
    },
    [selectedProcessId, pendingLinkDraft, addNotification, load, loadPending, applyEssayEligFromResponse]
  );

  const handleDiscardPending = useCallback(
    async (pendingId) => {
      if (!window.confirm('Descartar este arquivo da fila de pendências?')) return;
      const { id: pid } = parseAdminSelectedProcessId(selectedProcessId);
      if (pid == null) return;
      setPendingBusy(pendingId);
      try {
        const { error } = await apiClient.discardExamObjectiveScanPending(pid, pendingId);
        if (error) throw new Error(error);
        await loadPending();
      } catch (err) {
        addNotification('Erro', err.message || 'Falha', 'error');
      } finally {
        setPendingBusy(null);
      }
    },
    [selectedProcessId, addNotification, loadPending]
  );

  const handleRemoveScan = useCallback(
    async (app) => {
      if (!window.confirm('Remover o vínculo da folha escaneada desta inscrição?')) return;
      setScanBusy(app.id);
      try {
        const { error } = await apiClient.deleteExamObjectiveScan(app.id);
        if (error) throw new Error(error);
        addNotification('Removido', 'Vínculo da folha excluído.', 'success');
        await load();
      } catch (err) {
        addNotification('Erro', err.message || 'Falha ao remover', 'error');
      } finally {
        setScanBusy(null);
      }
    },
    [addNotification, load]
  );

  if (loading && rows.length === 0) {
    return <LoadingSpinner text="Carregando prova objetiva…" />;
  }

  return (
    <div style={{ ...styles.contentContainer, padding: '16px' }}>
      <h2 style={styles.cardTitle}>Prova objetiva</h2>
      <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 16, maxWidth: 820, lineHeight: 1.55 }}>
        <strong>Um PDF por candidato.</strong> Envio em lote: nomeie o arquivo com o número da inscrição (ex.:{' '}
        <code>123.pdf</code>, <code>123 prova objetiva.pdf</code>, <code>inscricao_123.pdf</code> ou{' '}
        <code>ING0000000123.pdf</code>) ou use o código da folha.
        Arquivos não identificados vão para <strong>pendências</strong>. Com notas automáticas ativas no processo, o OMR
        roda após o vínculo quando o <strong>gabarito definitivo</strong> estiver salvo no processo. Substitua PDFs errados a qualquer momento.
      </p>

      <div style={{ ...styles.card, marginBottom: 16 }}>
        <label style={styles.label}>Processo seletivo</label>
        <select value={selectedProcessId} onChange={setSelectedProcessId} style={{ ...styles.select, maxWidth: 420 }}>
          <option value="">Selecione o processo</option>
          {provaProcesses.map((p) => (
            <option key={p.id} value={String(p.id)}>
              {p.name}
            </option>
          ))}
        </select>
        {selectedProcessMeta && (
          <p style={{ marginTop: 10, fontSize: 12, color: '#374151' }}>
            Configuração:{' '}
            <strong style={{ color: manualGrades ? '#166534' : '#9CA3AF' }}>
              Manual {manualGrades ? 'ativo' : 'inativo'}
            </strong>
            {' · '}
            <strong style={{ color: autoGrades ? '#166534' : '#9CA3AF' }}>
              Automático {autoGrades ? 'ativo' : 'inativo'}
            </strong>
            {!manualGrades && (
              <span style={{ color: '#B45309' }}>
                {' '}
                — ative <em>Manual</em> em Configuração → Processos, salve e reabra esta página.
              </span>
            )}
          </p>
        )}
        {provaProcesses.length === 0 && (
          <p style={{ marginTop: 10, fontSize: 12, color: '#B45309' }}>
            Nenhum processo com folha objetiva (gabarito). Ative em Processos → Prova — folhas (PDF).
          </p>
        )}
        {selectedPid != null && !definitiveKeyReady && (
          <div
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 8,
              border: '1px solid #FCD34D',
              background: '#FFFBEB',
              fontSize: 13,
              color: '#92400E',
              lineHeight: 1.5,
            }}
          >
            {DEFINITIVE_ANSWER_KEY_REQUIRED_MSG}
          </div>
        )}
        {autoGrades && selectedPid != null && (
          <>
            <input
              ref={omrCalFileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              style={{ display: 'none' }}
              onChange={handleOmrCalFileSelected}
            />
            <div
              style={{
                marginTop: 12,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 10,
                alignItems: 'center',
              }}
            >
              <button
                type="button"
                disabled={!definitiveKeyReady}
                title={!definitiveKeyReady ? DEFINITIVE_ANSWER_KEY_REQUIRED_MSG : undefined}
                onClick={handleCalibrarOmrClick}
                style={{
                  ...styles.button,
                  ...styles.buttonSecondary,
                  opacity: !definitiveKeyReady ? 0.7 : 1,
                }}
              >
                Calibrar OMR
              </button>
              {omrCalSummary?.active && (
                <span
                  style={{
                    fontSize: 12,
                    color: '#166534',
                    background: '#ECFDF5',
                    border: '1px solid #A7F3D0',
                    borderRadius: 999,
                    padding: '4px 12px',
                  }}
                >
                  Calibração ativa
                  {omrCalSummary.reference_filename
                    ? ` · ${omrCalSummary.reference_filename}`
                    : omrCalSummary.reference_application_id
                      ? ` · ref. #${omrCalSummary.reference_application_id}`
                      : ''}
                </span>
              )}
              <button
                type="button"
                disabled={omrBusy || !definitiveKeyReady}
                title={!definitiveKeyReady ? DEFINITIVE_ANSWER_KEY_REQUIRED_MSG : undefined}
                onClick={handleRunOmrBatch}
                style={{
                  ...styles.button,
                  ...styles.buttonSecondary,
                  opacity: omrBusy || !definitiveKeyReady ? 0.7 : 1,
                }}
              >
                {omrBusy ? 'Processando OMR…' : 'Reprocessar notas automáticas (todas com PDF)'}
              </button>
            </div>
            <ExamOmrCalibrationModal
              isOpen={omrCalibrateOpen}
              onClose={() => {
                setOmrCalibrateOpen(false);
                setOmrCalUpload(null);
              }}
              processId={selectedPid}
              definitiveAnswerKeyReady={definitiveKeyReady}
              onCalibrationUpdated={setOmrCalSummary}
              initialReferenceFilename={omrCalUpload?.reference_filename}
              initialReview={omrCalUpload?.review}
              previewVersion={omrCalUpload?.previewVersion ?? 0}
              previewStaging={Boolean(omrCalUpload?.staging)}
              onRequestReplaceFile={() => omrCalFileInputRef.current?.click()}
            />
          </>
        )}
        {usesEssay && selectedPid != null && (
          <div
            style={{
              marginTop: 14,
              padding: 12,
              borderRadius: 8,
              border: '1px solid #C4B5FD',
              background: '#F5F3FF',
            }}
          >
            <p style={{ margin: '0 0 8px 0', fontSize: 13, color: '#4C1D95', lineHeight: 1.5 }}>
              <strong>Corte da redação</strong> (multiplicador ×{essayMultiplier} × vagas — grupos AC e cotas). Recalculado
              automaticamente após OMR, lote ou lançamento manual de notas objetivas.
            </p>
            <button
              type="button"
              disabled={eligRecalcBusy}
              onClick={handleRecalcEssayEligibility}
              style={{ ...styles.button, ...styles.buttonSecondary, opacity: eligRecalcBusy ? 0.7 : 1 }}
            >
              {eligRecalcBusy ? 'Recalculando…' : 'Recalcular corte da redação'}
            </button>
            {essayEligSummary?.ok && (
              <p style={{ margin: '10px 0 0 0', fontSize: 12, color: '#5B21B6' }}>{essayEligSummaryText(essayEligSummary)}</p>
            )}
          </div>
        )}
      </div>

      <div
        style={{
          ...styles.card,
          marginBottom: 16,
          border: '1px solid #BFDBFE',
          background: '#F8FAFC',
        }}
      >
        <h3 style={{ ...styles.cardTitle, fontSize: 16, margin: '0 0 8px 0' }}>Importação em lote de folhas</h3>
        <p style={{ fontSize: 13, color: '#4B5563', margin: '0 0 12px 0', lineHeight: 1.5 }}>
          Escolha vários PDFs (um candidato por arquivo).
        </p>
        <input
          id={batchFileInputId}
          ref={batchInputRef}
          type="file"
          accept="application/pdf,.pdf"
          multiple
          style={{ display: 'none' }}
          disabled={!batchUploadReady}
          onChange={handleBatchSelected}
        />
        {batchUploadReady ? (
          <label
            htmlFor={batchFileInputId}
            style={{
              ...styles.button,
              ...styles.buttonPrimary,
              display: 'inline-block',
              cursor: batchBusy ? 'wait' : 'pointer',
              margin: 0,
            }}
          >
            {batchBusy ? 'Enviando lote…' : 'Escolher PDFs e enviar lote'}
          </label>
        ) : (
          <button
            type="button"
            disabled={batchBusy}
            onClick={warnBatchUploadBlocked}
            style={{
              ...styles.button,
              ...styles.buttonPrimary,
              opacity: 0.65,
            }}
          >
            Escolher PDFs e enviar lote
          </button>
        )}
        {selectedPid == null && (
          <p style={{ marginTop: 8, fontSize: 12, color: '#B45309' }}>Selecione o processo acima para habilitar.</p>
        )}
      </div>

      {selectedPid != null && manualGrades && (
        <>
          <div style={{ ...styles.card, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={styles.cardTitle}>Filtros</h3>
              <button
                type="button"
                onClick={clearAdminFilters}
                style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 12px', fontSize: 13 }}
              >
                Limpar filtros
              </button>
            </div>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Candidato</label>
                <input
                  type="text"
                  value={filterCandidate}
                  onChange={(e) => setFilterCandidate(e.target.value)}
                  placeholder="Nome, CPF…"
                  style={{ ...styles.select, fontFamily: 'inherit' }}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Analista (notas objetivas)</label>
                <select value={filterAnalyst} onChange={(e) => setFilterAnalyst(e.target.value)} style={styles.select}>
                  <option value="">Todos</option>
                  <option value="none">Sem analista</option>
                  {analystFilterOptions.map((a) => (
                    <option key={a.id} value={String(a.id)}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Atribuição</label>
                <select value={filterAssignment} onChange={(e) => setFilterAssignment(e.target.value)} style={styles.select}>
                  <option value="">Todas</option>
                  <option value="not_assigned">Sem analista atribuído</option>
                  <option value="assigned">Com analista</option>
                  <option value="eligible">Elegíveis à atribuição</option>
                </select>
              </div><div style={styles.formGroup}>
                <label style={styles.label}>Curso</label>
                <select value={filterCourse} onChange={(e) => setFilterCourse(e.target.value)} style={styles.select}>
                  <option value="">Todos</option>
                  {courseFilterOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Unidade</label>
                <select value={filterCampus} onChange={(e) => setFilterCampus(e.target.value)} style={styles.select}>
                  <option value="">Todas</option>
                  {campusFilterOptions.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Cota</label>
                <select value={filterQuota} onChange={(e) => setFilterQuota(e.target.value)} style={styles.select}>
                  <option value="">Todas</option>
                  {quotaFilterOptions.map((q) => (
                    <option key={q} value={q}>
                      {q}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div style={{ ...styles.card, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <h3 style={styles.cardTitle}>Inscrições</h3>
                <span style={{ fontSize: 12, color: '#6B7280' }}>
                  Exibindo {filteredRows.length} de {rows.length} | Elegíveis à atribuição: {pendingForBulk.length}
                </span>
              </div>
              {pendingForBulk.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 10, maxWidth: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={() => setSelectedForBulk(new Set(pendingForBulk.map((x) => x.id)))}
                      style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 10px', fontSize: 12 }}
                    >
                      Selecionar todos ({pendingForBulk.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedForBulk(new Set())}
                      style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 10px', fontSize: 12 }}
                    >
                      Desmarcar
                    </button>
                    <select value={bulkAnalyst} onChange={(e) => setBulkAnalyst(e.target.value)} style={{ ...styles.select, minWidth: 180 }}>
                      <option value="">Atribuir a…</option>
                      {analystsForProcess.map((a) => (
                        <option key={a.id} value={String(a.id)}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={handleBulkAssign}
                      disabled={!bulkAnalyst || selectedForBulk.size === 0}
                      style={{ ...styles.button, ...styles.buttonPrimary, padding: '6px 12px', fontSize: 12 }}
                    >
                      Atribuir selecionados ({selectedForBulk.size})
                    </button>
                  </div>
                  <div style={{ paddingTop: 8, borderTop: '1px solid #E5E7EB' }}>
                    <div style={{ fontSize: 12, color: '#374151', fontWeight: 600, marginBottom: 4 }}>
                      Distribuição equânime (rodízio)
                    </div>
                    <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 8 }}>Marque 2 ou mais analistas.</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginBottom: 8 }}>
                      {analystsForProcess.map((a) => (
                        <label key={a.id} style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={equitableAnalysts.has(String(a.id))}
                            onChange={(e) => {
                              setEquitableAnalysts((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(String(a.id));
                                else next.delete(String(a.id));
                                return next;
                              });
                            }}
                          />
                          {a.name}
                        </label>
                      ))}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      <button
                        type="button"
                        onClick={() => setEquitableAnalysts(new Set(analystsForProcess.map((x) => String(x.id))))}
                        style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 10px', fontSize: 11 }}
                      >
                        Marcar todos os analistas do processo
                      </button>
                      <button
                        type="button"
                        onClick={() => setEquitableAnalysts(new Set())}
                        style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 10px', fontSize: 11 }}
                      >
                        Limpar marcação
                      </button>
                      <button
                        type="button"
                        onClick={handleBulkAssignEquitable}
                        disabled={selectedForBulk.size === 0 || equitableAnalysts.size < 2}
                        style={{
                          ...styles.button,
                          ...styles.buttonPrimary,
                          padding: '6px 12px',
                          fontSize: 12,
                          opacity: selectedForBulk.size === 0 || equitableAnalysts.size < 2 ? 0.55 : 1,
                        }}
                      >
                        Distribuir ({selectedForBulk.size} inscrições)
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <input ref={fileInputRef} type="file" accept="application/pdf,.pdf" style={{ display: 'none' }} onChange={handleFileSelected} />

      {scanActionMsg && (
        <div
          style={{
            ...styles.alert,
            marginBottom: 16,
            ...(scanActionMsg.type === 'error'
              ? styles.alertError
              : scanActionMsg.type === 'warning'
                ? { background: '#FFFBEB', borderColor: '#FCD34D', color: '#92400E' }
                : scanActionMsg.type === 'info'
                  ? { background: '#EFF6FF', borderColor: '#93C5FD', color: '#1E3A8A' }
                  : styles.alertSuccess),
          }}
          role="status"
        >
          {scanActionMsg.text}
        </div>
      )}

      <ExamPendingScansPanel
        pendingRows={pendingRows}
        pendingLinkDraft={pendingLinkDraft}
        setPendingLinkDraft={setPendingLinkDraft}
        pendingBusy={pendingBusy}
        onLink={handleLinkPending}
        onDiscard={handleDiscardPending}
      />

      <div style={{ overflowX: 'auto' }}>
        <table style={styles.table}>
          <thead style={styles.tableHeader}>
            <tr>
              {manualGrades && pendingForBulk.length > 0 && (
                <th style={{ ...styles.tableHeaderCell, width: 36 }} aria-label="Seleção" />
              )}
              <th style={styles.tableHeaderCell}>#</th>
              <th
                style={{ ...styles.tableHeaderCell, cursor: 'pointer' }}
                onClick={() => setTableSort((p) => nextAdminTableSort(p, 'candidate'))}
              >
                Candidato{sortColumnIndicator(tableSort, 'candidate')}
              </th>
              <th style={styles.tableHeaderCell}>Curso</th>
              <th style={{ ...styles.tableHeaderCell, textAlign: 'center', minWidth: 48 }}>Cota</th>
                            <th style={styles.tableHeaderCell}>Folha escaneada</th>
              <th style={styles.tableHeaderCell}>Nota objetiva</th>
              <th style={styles.tableHeaderCell}>Lançado por</th>
              {usesEssay && <th style={styles.tableHeaderCell}>Corte redação</th>}
              {manualGrades && <th style={styles.tableHeaderCell}>Atribuição</th>}
              {manualGrades && <th style={styles.tableHeaderCell}>Ações</th>}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((app) => (
              <tr key={app.id} style={styles.tableRow}>
                {manualGrades && pendingForBulk.length > 0 && (
                  <td style={styles.tableCell}>
                    {canAssignExamGrader(app) ? (
                      <input
                        type="checkbox"
                        checked={selectedForBulk.has(app.id)}
                        onChange={(e) => {
                          setSelectedForBulk((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(app.id);
                            else next.delete(app.id);
                            return next;
                          });
                        }}
                        aria-label={`Selecionar inscrição ${app.id}`}
                      />
                    ) : null}
                  </td>
                )}
                <td style={styles.tableCell}>#{app.id}</td>
                <td style={styles.tableCell}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{app.candidate_name || '—'}</div>
                  <div style={{ fontSize: 11, color: '#6B7280' }}>{app.candidate_cpf || '—'}</div>
                </td>
                <td style={styles.tableCell}>{app.course_name || '—'}</td>
                <td style={{ ...styles.tableCell, textAlign: 'center', fontSize: 12 }}>{app.quota_type || 'AC'}</td>
                  <td style={styles.tableCell}>
                    <ExamScanCellActions
                      scanUrl={app.exam_objective_scan_url}
                      appId={app.id}
                      scanBusy={scanBusy}
                      onUpload={triggerUpload}
                      onRemove={() => handleRemoveScan(app)}
                      uploadDisabled={!definitiveKeyReady}
                      uploadDisabledTitle={DEFINITIVE_ANSWER_KEY_REQUIRED_MSG}
                    />
                  </td>
                <td style={{ ...styles.tableCell, textAlign: 'center' }}>
                  <ExamObjectiveScoreCell
                    app={app}
                    scoreMode={selectedProcessMeta?.exam_objective_score_mode}
                  />
                </td>
                <td style={{ ...styles.tableCell, fontSize: 12 }}>{gradeSourceLabel(app)}</td>
                {usesEssay && (
                  <td style={{ ...styles.tableCell, fontSize: 12, textAlign: 'center' }}>
                    {(() => {
                      const cut = essayCutDisplay(app);
                      return (
                        <span title={cut.detail}>
                          <strong style={{ color: cut.color }}>{cut.label}</strong>
                          {cut.detail ? (
                            <span style={{ display: 'block', fontSize: 10, color: '#6B7280' }}>{cut.detail}</span>
                          ) : null}
                        </span>
                      );
                    })()}
                  </td>
                )}
                {manualGrades && (
                  <td style={styles.tableCell}>
                    <span
                      style={{
                        ...getStatusBadgeStyle(app.exam_grade_analyst_id ? 'assigned' : 'not_assigned'),
                        fontSize: '9px',
                        padding: '4px 8px',
                        textTransform: 'none',
                        letterSpacing: 0,
                      }}
                      title={app.exam_grade_analyst_name || getAssignmentStatusLabel('not_assigned')}
                    >
                      {app.exam_grade_analyst_name || getAssignmentStatusLabel('not_assigned')}
                    </span>
                    {app.exam_objective_assignment_status &&
                      String(app.exam_objective_assignment_status).toLowerCase() !== 'completed' && (
                      <span style={{ ...examStatusStyle(app.exam_objective_assignment_status), display: 'block', marginTop: 4 }}>
                        {examObjectiveStatusLabel(app.exam_objective_assignment_status)}
                      </span>
                    )}
                  </td>
                )}
                {manualGrades && (
                  <td style={{ ...styles.tableCell, whiteSpace: 'normal', fontSize: 10, padding: '8px 10px' }}>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                      {canAssignExamGrader(app) && (
                        <button
                          type="button"
                          style={{ ...getButtonStyle('assignment', 'small'), padding: '3px 5px', fontSize: '9px' }}
                          onClick={() => openAssign(app)}
                        >
                          {ACTION_ICONS.assign} Atribuir
                        </button>
                      )}
                      {canReassignExamGrader(app) && (
                        <button
                          type="button"
                          style={{ ...getButtonStyle('secondary', 'small'), padding: '3px 6px', fontSize: '9px' }}
                          onClick={() => openAssign(app, true)}
                        >
                          {ACTION_ICONS.reassign} Reatribuir
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={!definitiveKeyReady}
                        title={!definitiveKeyReady ? DEFINITIVE_ANSWER_KEY_REQUIRED_MSG : undefined}
                        onClick={() =>
                          definitiveKeyReady &&
                          setGradeApp(enrichApplicationForGradeModal(app, selectedProcessMeta))
                        }
                        style={{
                          ...getButtonStyle('primary', 'small'),
                          padding: '3px 6px',
                          fontSize: '9px',
                          opacity: definitiveKeyReady ? 1 : 0.5,
                          cursor: definitiveKeyReady ? 'pointer' : 'not-allowed',
                        }}
                      >
                        Lançar acertos
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {sortedRows.length === 0 && (
          <p style={{ marginTop: 12, color: '#6B7280' }}>Nenhuma inscrição neste processo.</p>
        )}
      </div>

      <Modal
        isOpen={showAssignmentModal}
        onClose={() => {
          setShowAssignmentModal(false);
          setAssignmentApp(null);
          setAssignmentIsReassign(false);
          setSelectedAnalyst('');
          setAssignmentModalError('');
        }}
        title={
          assignmentIsReassign
            ? 'Reatribuir — lançamento de notas objetivas'
            : 'Atribuir analista — lançamento de notas objetivas'
        }
      >
        {assignmentApp && (
          <div style={{ padding: '8px 4px 16px' }}>
            <p style={{ fontSize: 13, color: '#4B5563', margin: '0 0 12px' }}>
              Inscrição #{assignmentApp.id} — {assignmentApp.candidate_name}
            </p>
            {assignmentModalError && (
              <p style={{ color: '#B91C1C', fontSize: 13, marginBottom: 12 }}>{assignmentModalError}</p>
            )}
            <label style={styles.label}>Analista</label>
            <select
              value={selectedAnalyst}
              onChange={(e) => setSelectedAnalyst(e.target.value)}
              style={{ ...styles.select, width: '100%', marginBottom: 16 }}
            >
              <option value="">Selecione…</option>
              {analystsForProcess.map((a) => (
                <option key={a.id} value={String(a.id)}>
                  {a.name}
                </option>
              ))}
            </select>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                style={{ ...styles.button, ...styles.buttonSecondary }}
                onClick={() => setShowAssignmentModal(false)}
              >
                Cancelar
              </button>
              <button type="button" style={{ ...styles.button, ...styles.buttonPrimary }} onClick={submitAssignment}>
                {assignmentIsReassign ? `${ACTION_ICONS.reassign} Reatribuir` : `${ACTION_ICONS.assign} Atribuir`}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <ExamGradeEntryModal
        isOpen={Boolean(gradeApp)}
        application={gradeApp}
        processId={parseAdminSelectedProcessId(selectedProcessId).id}
        definitiveAnswerKeyReady={definitiveKeyReady}
        onClose={() => setGradeApp(null)}
        onSaved={(payload) => {
          applyEssayEligFromResponse(payload?.essay_eligibility, {
            notify: Boolean(payload?.essay_eligibility?.ok),
          });
          load();
        }}
      />
    </div>
  );
});

